import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, FileText } from 'lucide-react';
import SEOHelmet from '../components/SEOHelmet';

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHelmet 
        title="Privacy Policy & Terms"
        description="Privacy Policy and Terms & Conditions for the South African FinTech Awards 2025. Learn about how we protect your data and our terms of service."
        keywords="privacy policy, terms and conditions, data protection, POPIA compliance, fintech awards privacy"
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              <Shield className="h-16 w-16" style={{ color: '#C1B3A3' }} />
            </div>
            <h1 className="text-4xl font-bold mb-6" style={{ color: '#C1B3A3' }}>
              Privacy Policy & Terms
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your privacy is important to us. Learn about how we protect your data and our terms of service.
            </p>
            <p className="text-gray-400 mt-4">Last Updated: {currentDate}</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-8">
              <div className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold mb-6 flex items-center">
                  <Lock className="h-6 w-6 mr-2" style={{ color: '#C1B3A3' }} />
                  Privacy Policy
                </h2>

                <div className="space-y-6 text-gray-600">
                  <p className="font-semibold">
                    The <strong>South African FinTech Awards</strong> ("SA FinTech Awards"), owned and operated by <strong>titc.io</strong> ("we," "us," "our"), is committed to protecting the privacy and personal information of our users.
                  </p>

                  <h3 className="text-xl font-bold mt-8 mb-4">1. Information We Collect</h3>
                  <h4 className="font-semibold mb-2">1.1 Personal Information Provided by You</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Nomination Submissions:</strong> Name, email address, company details, contact number, and other identifying details of nominees and nominators.</li>
                    <li><strong>User Registration:</strong> When you sign up for updates or create an account, we collect your name, email, and other necessary details.</li>
                    <li><strong>Event Participation:</strong> If you attend our events, we may collect information necessary for event registration and participation.</li>
                    <li><strong>Marketing Communications:</strong> If you subscribe to newsletters or marketing materials, we collect your contact details.</li>
                  </ul>

                  <h4 className="font-semibold mb-2">1.2 Information Collected Automatically</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Website Usage Data:</strong> We may collect non-personal information, such as IP addresses, browser types, cookies, and analytics data, to improve user experience.</li>
                    <li><strong>Cookies and Tracking Technologies:</strong> We use cookies to analyze website traffic and enhance functionality. Users can disable cookies in their browser settings.</li>
                  </ul>

                  <h3 className="text-xl font-bold mt-8 mb-4">2. How We Use Your Information</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Award Nominations & Judging:</strong> To evaluate, manage, and administer the awards process.</li>
                    <li><strong>Event Management:</strong> To facilitate event participation, ticketing, and guest registration.</li>
                    <li><strong>Communications & Marketing:</strong> To provide updates, promotional material, and relevant content (users may opt out at any time).</li>
                    <li><strong>Legal & Compliance:</strong> To comply with legal obligations, resolve disputes, and enforce agreements.</li>
                    <li><strong>Security & Fraud Prevention:</strong> To protect our website, users, and ensure secure transactions.</li>
                  </ul>

                  <h3 className="text-xl font-bold mt-8 mb-4">3. Data Sharing & Disclosure</h3>
                  <p>We do not sell personal data. However, we may share information with:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Event Partners & Sponsors (where explicit consent is given)</li>
                    <li>Third-Party Service Providers (e.g., email, hosting, and analytics services)</li>
                    <li>Legal Authorities (if required by law or for fraud prevention)</li>
                  </ul>

                  <h2 className="text-2xl font-bold mt-12 mb-6 flex items-center">
                    <FileText className="h-6 w-6 mr-2" style={{ color: '#C1B3A3' }} />
                    Terms & Conditions
                  </h2>

                  <h3 className="text-xl font-bold mb-4">4. Nominations Disclaimer</h3>
                  <p>By submitting a nomination:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You confirm that all information provided is accurate and does not infringe on the rights of any third party.</li>
                    <li>You indemnify <strong>titc.io</strong>, its affiliates, and the SA FinTech Awards against any legal claims, disputes, or liabilities arising from the nomination process.</li>
                    <li>We reserve the right to verify or disqualify nominations at our sole discretion.</li>
                    <li>We assume no responsibility for incorrect, misleading, or fraudulent information provided in nominations.</li>
                  </ul>

                  <h3 className="text-xl font-bold mt-8 mb-4">5. Your Rights Under POPIA</h3>
                  <p>As a data subject under POPIA, you have the right to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Access, correct, or delete your personal information.</li>
                    <li>Object to processing for direct marketing purposes.</li>
                    <li>Withdraw consent where processing is based on consent.</li>
                    <li>Lodge a complaint with the Information Regulator of South Africa.</li>
                  </ul>

                  <div className="mt-12 p-6 bg-gray-50 rounded-lg">
                    <h3 className="text-xl font-bold mb-4">Contact Us</h3>
                    <p>For any questions or concerns regarding this Privacy Policy, please contact us at:</p>
                    <div className="mt-4">
                      <p><strong>titc.io</strong></p>
                      <p>Email: <a href="mailto:info@titc.io" className="text-indigo-600 hover:text-indigo-800">info@titc.io</a></p>
                      <p>Phone: +27 21 012 5989</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;